/**
 * Membership upgrade server functions (thin wrappers only — all logic lives
 * in `./membership-upgrade.server.ts`).
 */

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const planSchema = z.object({
  plan_id: z.enum(["membership_6month", "membership_yearly"]),
});

export const getMyUpgradeOptions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { loadUpgradeState, describeCurrentPlan, resolveUpgradeProvider } = await import(
      "./membership-upgrade.server"
    );
    const { MEMBERSHIP_PLANS } = await import("./membership-plans");

    const { row, paypal } = await loadUpgradeState(supabaseAdmin, context.userId);
    const current = await describeCurrentPlan(supabaseAdmin, row, paypal);
    const provider = resolveUpgradeProvider(row, paypal);

    const options = MEMBERSHIP_PLANS.filter(
      (p) => p.id === "membership_6month" || p.id === "membership_yearly",
    )
      .filter((p) => p.id !== current.planId)
      .map((p) => ({
        id: p.id,
        name: p.shortName,
        price_cents: p.priceCents,
        billing_note: p.billingNote,
        saving_note: p.savingNote ?? null,
        equivalent_note: p.equivalentNote ?? null,
      }));

    const currentPlan = MEMBERSHIP_PLANS.find((p) => p.id === current.planId) ?? null;

    return {
      status: row?.status ?? null,
      current_plan_id: current.planId,
      current_plan_label: current.label,
      current_plan_price_cents: currentPlan?.priceCents ?? null,
      current_plan_billing_note: currentPlan?.billingNote ?? null,
      provider,
      next_renewal: row?.current_period_end ?? paypal?.next_billing_time ?? null,
      cancel_at_period_end: row?.cancel_at_period_end ?? false,
      upgraded_at: row?.upgraded_at ?? null,
      options,
    };
  });

export const previewMyStripeUpgrade = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => planSchema.parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const { previewStripeUpgradeCharge } = await import("./membership-upgrade.server");
    try {
      return await previewStripeUpgradeCharge({ userId: context.userId, planId: data.plan_id });
    } catch (err) {
      console.error("[upgrade] preview failed", err);
      return null;
    }
  });

export const upgradeMyStripeMembership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => planSchema.parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const { performStripeUpgrade } = await import("./membership-upgrade.server");
    try {
      return await performStripeUpgrade({ userId: context.userId, planId: data.plan_id });
    } catch (err) {
      console.error("[upgrade] stripe upgrade failed", err);
      return {
        ok: false as const,
        reason: "stripe_error",
        message:
          "We couldn't complete the upgrade with your saved card. Nothing was charged twice — please try again or contact us.",
      };
    }
  });

const paypalStartSchema = planSchema.extend({
  return_url: z.string().url(),
  cancel_url: z.string().url(),
});

export const startMyPaypalUpgrade = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => paypalStartSchema.parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const { startPaypalUpgrade } = await import("./membership-upgrade.server");
    try {
      return await startPaypalUpgrade({
        userId: context.userId,
        planId: data.plan_id,
        returnUrl: data.return_url,
        cancelUrl: data.cancel_url,
      });
    } catch (err) {
      console.error("[upgrade] paypal revise failed", err);
      return {
        ok: false as const,
        reason: "paypal_error",
        message:
          "We couldn't open the PayPal upgrade. Your current membership is unchanged — please try again shortly.",
      };
    }
  });

export const finaliseMyPaypalUpgrade = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => planSchema.parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const { finalisePaypalUpgrade } = await import("./membership-upgrade.server");
    try {
      return await finalisePaypalUpgrade({ userId: context.userId, planId: data.plan_id });
    } catch (err) {
      console.error("[upgrade] paypal finalise failed", err);
      return {
        ok: false as const,
        reason: "paypal_error",
        message: "We couldn't confirm the upgrade with PayPal yet. Please refresh in a few minutes.",
      };
    }
  });
